import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/hooks/useAuth";
import { useForceRefreshListener } from "@/hooks/useForceRefreshListener";
import Auth from "./pages/Auth";
import ProtectedRoute from "./components/console/ProtectedRoute";

// Lazy-loaded route components for code splitting
const ConversationLayout = lazy(() => import("./components/console/layouts/ConversationLayout"));
const SinglePanelLayout = lazy(() => import("./components/console/layouts/SinglePanelLayout"));
const AnalyticsDashboard = lazy(() => import("./components/console/AnalyticsDashboard").then(m => ({ default: m.AnalyticsDashboard })));
const BillingPanel = lazy(() => import("./components/console/BillingPanel").then(m => ({ default: m.BillingPanel })));
const SettingsPanel = lazy(() => import("./components/console/SettingsPanel").then(m => ({ default: m.SettingsPanel })));
const SuperAdminPanel = lazy(() => import("./components/console/SuperAdminPanel").then(m => ({ default: m.SuperAdminPanel })));
const GuestsPage = lazy(() => import("./components/console/guests/GuestsPage").then(m => ({ default: m.GuestsPage })));
const AccountPage = lazy(() => import("./components/console/AccountPage").then(m => ({ default: m.AccountPage })));
const GuidePage = lazy(() => import("./components/console/GuidePage").then(m => ({ default: m.GuidePage })));
const ScheduledPage = lazy(() => import("./components/console/ScheduledPage").then(m => ({ default: m.ScheduledPage })));

const AutoLogin = lazy(() => import("./pages/AutoLogin"));
const WidgetDemo = lazy(() => import("./pages/WidgetDemo"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient();

function ForceRefreshGate({ children }: { children: React.ReactNode }) {
  useForceRefreshListener();
  return <>{children}</>;
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <AuthProvider>
        <ForceRefreshGate>
          <Toaster />
          <Sonner />
           <BrowserRouter>
            <Suspense fallback={<div className="flex h-screen items-center justify-center"><div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" /></div>}>
             <Routes>
               <Route path="/" element={<Navigate to="/inbox" replace />} />
               <Route path="/auth" element={<Auth />} />
               <Route path="/auto-login" element={<AutoLogin />} />
               <Route path="/inbox" element={<ProtectedRoute><ConversationLayout /></ProtectedRoute>} />
               <Route path="/guests" element={<ProtectedRoute><SinglePanelLayout current="guests"><GuestsPage /></SinglePanelLayout></ProtectedRoute>} />
               <Route path="/programmati" element={<ProtectedRoute><SinglePanelLayout current="programmati"><ScheduledPage /></SinglePanelLayout></ProtectedRoute>} />
               <Route path="/analytics" element={<ProtectedRoute minRole="supervisor"><SinglePanelLayout current="analytics"><AnalyticsDashboard /></SinglePanelLayout></ProtectedRoute>} />
                <Route path="/billing" element={<ProtectedRoute minRole="supervisor"><SinglePanelLayout current="billing"><BillingPanel /></SinglePanelLayout></ProtectedRoute>} />
                <Route path="/settings" element={<ProtectedRoute minRole="supervisor"><SinglePanelLayout current="settings"><SettingsPanel /></SinglePanelLayout></ProtectedRoute>} />
               <Route path="/superadmin" element={<ProtectedRoute minRole="superadmin"><SinglePanelLayout current="superadmin"><SuperAdminPanel /></SinglePanelLayout></ProtectedRoute>} />
               
              <Route path="/account" element={<ProtectedRoute><SinglePanelLayout current="account"><AccountPage /></SinglePanelLayout></ProtectedRoute>} />
               <Route path="/guida" element={<ProtectedRoute><SinglePanelLayout current="guida"><GuidePage /></SinglePanelLayout></ProtectedRoute>} />
               <Route path="/widget-demo" element={<WidgetDemo />} />
               <Route path="*" element={<NotFound />} />
             </Routes>
            </Suspense>
           </BrowserRouter>
        </ForceRefreshGate>
      </AuthProvider>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
